import { Component, Input, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { cloneDeep } from 'lodash';
import { DomManagementService } from '@rappider/services';
import {
  PageWithRelations,
  ComponentDefinitionWithRelations,
  ProjectTheme,
  Project
} from '@rappider/rappider-sdk';
import { ContentTree } from 'libs/content-tree-renderer/src/lib/models';
import { LoadPageWithComponents } from 'libs/content-editor/src/lib/state/content-editor.actions';
import { contentTreeWithRelations } from 'libs/content-editor/src/lib/selectors/content-tree-with-details.selector';
import { getComponentDefinitionsWithDetailsSelector } from '@rappider/shared';
import { getActiveLayoutForPage } from 'libs/content-editor/src/lib/selectors/layout-with-details.selector';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'rappider-ui-page-renderer',
  templateUrl: './ui-page-renderer.component.html',
  styleUrls: ['./ui-page-renderer.component.scss']
})
export class UiPageRendererComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * The ID of the page you want to render.
   */
  @Input() pageId: string;
  /**
   * The active project context, needed to fetch the correct theme.
   */
  @Input() activeProject: Project;

  // Data for the template
  public page: PageWithRelations;
  public layout: PageWithRelations;
  public contentTree: ContentTree;
  public componentDefinitions: ComponentDefinitionWithRelations[];
  public projectTheme: ProjectTheme;
  public isLoading = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<any>, // Inject the NgRx Store
    private domManagementService: DomManagementService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this.subscribeToRouteParams());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-fetch data from the store whenever the pageId input changes.
    if (changes.pageId && this.pageId) {
      this.getPageDataFromStore();
    }
    // Re-apply styles if the project context changes
    if (changes.activeProject && this.activeProject) {
      this.applyStyles();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions and styles.
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.removeStyleTags();
  }

  /**
   * Dispatches an action to load page data and subscribes to store selectors.
   */
  private getPageDataFromStore(): void {
    if (!this.pageId) {
      return;
    }
    // Unsubscribe from previous subscriptions to avoid memory leaks
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    // Dispatch the action to load the page and its components into the store
    this.store.dispatch(new LoadPageWithComponents({ pageId: this.pageId }));
    // Start subscribing to the store for updates
    this.subscribeToData();
  }

  /**
   * Sets up all the necessary subscriptions to the NgRx store.
   */
  private subscribeToData(): void {
    this.subscriptions = [
      this.subscribeToLoadingState(),
      this.subscribeToPage(),
      this.subscribeToLayout(),
      this.subscribeToContentTree(),
      this.subscribeToComponentDefinitions(),
      // this.subscribeToProjectTheme()
    ];
  }


  private subscribeToRouteParams() {
    return this.route.params.subscribe(params => {
      this.pageId = params['pageId'];
      this.getPageDataFromStore();
    });
  }

  private subscribeToLoadingState(): Subscription {
    return this.store.select(state => state.contentEditor?.loading)
      .subscribe(loading => {
        this.isLoading = loading;
      });
  }

  private subscribeToPage(): Subscription {
    return this.store.select(state => state.contentEditor?.page)
      .subscribe((page: PageWithRelations) => {
        if (page?.id === this.pageId) {
          this.page = cloneDeep(page);
          this.applyStyles();
        }
      });
  }

  private subscribeToLayout(): Subscription {
    // Using the selector to get the layout associated with the current page
    return this.store.select(getActiveLayoutForPage)
      .subscribe((layout: PageWithRelations) => {
        this.layout = cloneDeep(layout);
        this.applyStyles();
      });
  }

  private subscribeToContentTree(): Subscription {
    // Using the selector that returns the fully-processed content tree
    return this.store.select(contentTreeWithRelations)
      .subscribe((contentTree: ContentTree) => {
        if (contentTree?.length) {
          this.contentTree = cloneDeep(contentTree);
        }
      });
  }

  private subscribeToComponentDefinitions(): Subscription {
    return this.store.select(getComponentDefinitionsWithDetailsSelector)
      .subscribe((componentDefinitions: ComponentDefinitionWithRelations[]) => {
        if (componentDefinitions?.length) {
          this.componentDefinitions = cloneDeep(componentDefinitions);
        }
      });
  }

  private subscribeToProjectTheme(): Subscription {
    return this.store.select(state => state.projectTheme?.data)
      .subscribe((projectThemes: ProjectTheme[]) => {
        if (projectThemes?.length && this.activeProject?.activeProjectThemeId) {
          const activeTheme = projectThemes.find(theme => theme.id === this.activeProject.activeProjectThemeId);
          if (activeTheme) {
            this.projectTheme = {
              id: activeTheme.id,
              name: activeTheme.name,
              theme: {
                ...(activeTheme.theme ?? {}),
                ...(activeTheme.customTheme ?? {})
              }
            };
            this.applyStyles();
          }
        }
      });
  }

  /**
   * Applies global project styles and page-specific styles to the document's head.
   */
  private applyStyles(): void {
    this.removeStyleTags(); // Clear out old styles first

    // // Apply global project styles from the activeProject input
    // if (this.activeProject?.cssStyle) {
    //   this.domManagementService.appendStyleTagToHead('global-styles', this.activeProject.cssStyle);
    // }

    // Apply page-specific and layout-specific styles from the store data
    let pageCss = '';
    if (this.layout?.cssStyle) {
      pageCss += this.layout.cssStyle + '\n';
    }
    if (this.page?.cssStyle) {
      pageCss += this.page.cssStyle;
    }
    if (pageCss) {
      this.domManagementService.appendStyleTagToHead(pageCss);
    }
  }

  /**
   * Removes the style tags created by this component to avoid style leakage.
   */
  private removeStyleTags(): void {
    document.getElementById('page-styles')?.remove();
    document.getElementById('global-styles')?.remove();
  }
}
